import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Badge } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon, RefreshCw, ToggleLeft, ToggleRight, X } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingImageMiniGameWheel, fetchUploadImageMiniGameWheel, fetchEditImageMiniGameWheel, fetchDeleteImageMiniGameWheel, fetchToggleStatusImageMiniGameWheel } from '../../../../api/imageWheel';
import { toast } from 'react-toastify';

const ImageWheelMiniGameManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Load images khi component mount
  useEffect(() => {
    loadImages();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Hàm load danh sách image wheel
  const loadImages = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingImageMiniGameWheel();
      setImages(data);
      toast.success('✅ Tải danh sách image wheel thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải image: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('⚠️ Vui lòng chọn file ảnh!');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('⚠️ File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 5MB.');
        return;
      }

      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (image) => {
    setEditMode(true);
    setCurrentImage(image);
    setPreviewUrl(image.url);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit image
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('⚠️ Vui lòng chọn file ảnh!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentImage) {
        // Edit image
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        await fetchEditImageMiniGameWheel(currentImage._id, formData);
        toast.success('✅ Cập nhật image wheel thành công!');
      } else {
        // Upload new image
        await fetchUploadImageMiniGameWheel(selectedFile);
        toast.success('✅ Thêm image wheel mới thành công!');
      }
      
      setModalOpen(false);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa image
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa image này?')) {
      setLoading(true);
      try {
        await fetchDeleteImageMiniGameWheel(id);
        toast.success('✅ Xóa image wheel thành công!');
        loadImages();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Xử lý toggle status
  const handleToggleStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      await fetchToggleStatusImageMiniGameWheel(id);
      const newStatus = !currentStatus;
      toast.success(`✅ ${newStatus ? 'Kích hoạt' : 'Vô hiệu hóa'} image thành công!`);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi khi thay đổi trạng thái: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  // Count active/inactive images
  const activeCount = images.filter(img => img.status === true || img.status === 'active').length;
  const inactiveCount = images.length - activeCount;

  return (
    <Fragment>
      <Breadcrumbs parent="Wheel MiniGame" title="Image Wheel" mainTitle="Quản lý Image Wheel MiniGame" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-gradient-warning text-white mb-4 border-0 shadow">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <RefreshCw size={32} />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">Image Wheel MiniGame</h4>
                    <p className="mb-0 opacity-75">
                      Quản lý hình ảnh cho minigame vòng quay may mắn
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Statistics Cards */}
            {!loading && images.length > 0 && (
              <Row className="mb-4">
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-warning mb-2">{images.length}</h2>
                      <p className="text-muted mb-0">Tổng số hình ảnh</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-success mb-2">{activeCount}</h2>
                      <p className="text-muted mb-0">Đang hoạt động</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-danger mb-2">{inactiveCount}</h2>
                      <p className="text-muted mb-0">Đã tắt</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-info mb-2">1:1</h2>
                      <p className="text-muted mb-0">Tỷ lệ khuyến nghị</p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <ImageIcon size={24} className="me-2 text-warning" />
                      Danh sách Image Wheel
                    </h4>
                    <p className="text-muted mb-0 small">
                      Quản lý hình ảnh hiển thị trên vòng quay
                    </p>
                  </div>
                  <Button color="warning" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Thêm Image Mới
                  </Button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="text-center my-5 py-5">
                    <div className="spinner-border text-warning mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Đang tải dữ liệu...</h5>
                    <p className="text-muted small">Vui lòng chờ trong giây lát</p>
                  </div>
                )}

                {/* Empty State */}
                {!loading && images.length === 0 && (
                  <div className="text-center my-5 py-5">
                    <div className="mb-4">
                      <RefreshCw size={100} className="text-muted opacity-50" />
                    </div>
                    <h4 className="text-muted mb-2">Chưa có image wheel nào</h4>
                    <p className="text-muted mb-4">
                      Thêm hình ảnh đầu tiên cho vòng quay may mắn
                    </p>
                    <Button color="warning" size="lg" onClick={handleAddNew}>
                      <PlusCircle size={18} className="me-2" />
                      Thêm Image Đầu Tiên
                    </Button>
                  </div>
                )}

                {/* Images Grid */}
                {!loading && images.length > 0 && (
                  <Row>
                    {images.map((image, index) => {
                      const isActive = image.status === true || image.status === 'active';
                      
                      return (
                        <Col xl="3" lg="4" md="6" sm="12" key={image._id || index} className="mb-4">
                          <Card className={`shadow-sm border h-100 hover-lift ${!isActive ? 'opacity-75' : ''}`}>
                            {/* Image Container */}
                            <div className="position-relative overflow-hidden" style={{ paddingTop: '100%', backgroundColor: '#f8f9fa' }}>
                              <img
                                src={image.url}
                                alt={`Wheel Image ${index + 1}`}
                                className="position-absolute top-0 start-0 w-100 h-100 hover-zoom"
                                style={{ 
                                  objectFit: 'cover',
                                  filter: !isActive ? 'grayscale(100%)' : 'none'
                                }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/400x400?text=Wheel+Image';
                                }}
                              />
                              {/* Status Badge */}
                              <div className="position-absolute top-0 end-0 m-2">
                                <Badge color={isActive ? 'success' : 'danger'} className="px-3 py-2">
                                  {isActive ? '✓ Active' : '✗ Inactive'}
                                </Badge>
                              </div>
                              {/* Index Badge */}
                              <div className="position-absolute top-0 start-0 m-2">
                                <Badge color="warning" className="px-3 py-2">
                                  #{index + 1}
                                </Badge>
                              </div>
                            </div>

                            {/* Card Body */}
                            <CardBody className="bg-light">
                              <div className="mb-2">
                                <p className="mb-1 small text-muted">
                                  <strong>ID:</strong> {image._id?.slice(-8)}
                                </p>
                                <p className="mb-0 small">
                                  <strong>Trạng thái:</strong>{' '}
                                  <span className={isActive ? 'text-success' : 'text-danger'}>
                                    {isActive ? 'Đang hoạt động' : 'Đã tắt'}
                                  </span>
                                </p>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  color={isActive ? 'success' : 'secondary'}
                                  size="sm"
                                  onClick={() => handleToggleStatus(image._id, isActive)}
                                  title={isActive ? 'Vô hiệu hóa' : 'Kích hoạt'}
                                  className="flex-grow-1"
                                >
                                  {isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                  <span className="ms-1 small">
                                    {isActive ? 'Tắt' : 'Bật'}
                                  </span>
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                  title="Chỉnh sửa"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(image._id)}
                                  title="Xóa"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Image */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg" centered>
          <ModalHeader toggle={handleCloseModal} className="bg-light">
            <div className="d-flex align-items-center">
              <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3">
                {editMode ? <Edit size={20} className="text-warning" /> : <PlusCircle size={20} className="text-warning" />}
              </div>
              <div>
                <h5 className="mb-0">
                  {editMode ? '✏️ Chỉnh sửa Image Wheel' : '➕ Thêm Image Wheel Mới'}
                </h5>
                <small className="text-muted">
                  {editMode ? 'Cập nhật hình ảnh vòng quay' : 'Upload hình ảnh cho vòng quay may mắn'}
                </small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <FormGroup>
              <Label for="wheelImage" className="fw-bold fs-6 mb-3">
                <ImageIcon size={18} className="me-2 text-warning" />
                Chọn hình ảnh <span className="text-danger">*</span>
              </Label>
              <Input
                id="wheelImage"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="form-control-lg"
              />
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">
                  <strong>📌 Yêu cầu cho hình ảnh vòng quay:</strong>
                </small>
                <ul className="small text-muted mb-0 ps-3">
                  <li>Định dạng: JPG, PNG (khuyến nghị PNG với nền trong suốt)</li>
                  <li>Kích thước tối đa: 5MB</li>
                  <li>Tỷ lệ: 1:1 (vuông) - Ví dụ: 500x500px, 800x800px</li>
                  <li>Nội dung: Logo, biểu tượng, hoặc hình ảnh phần thưởng</li>
                  <li>Màu sắc: Tươi sáng, dễ nhìn khi quay</li>
                </ul>
              </div>
            </FormGroup>

            {/* Preview Section */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="fw-bold fs-6 mb-3">
                  🖼️ Xem trước Image Wheel:
                </Label>
                <div className="border rounded overflow-hidden shadow-sm">
                  <div className="position-relative" style={{ paddingTop: '100%', backgroundColor: '#f8f9fa' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="position-absolute top-0 start-0 w-100 h-100"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <div className="bg-light p-3 text-center border-top">
                    <Badge color="warning" className="px-3 py-2 me-2">
                      <RefreshCw size={12} className="me-1" />
                      Wheel Image
                    </Badge>
                    <Badge color="success" className="px-3 py-2">
                      ✓ Preview
                    </Badge>
                  </div>
                </div>
                <div className="alert alert-info mt-3 mb-0">
                  <small>
                    💡 <strong>Tip:</strong> Hình ảnh này sẽ hiển thị trên các ô của vòng quay. 
                    Đảm bảo hình ảnh rõ ràng và dễ nhận biết khi vòng quay đang chuyển động.
                  </small>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={handleCloseModal} disabled={loading}>
              <X size={16} className="me-2" />
              Hủy bỏ
            </Button>
            <Button 
              color="warning" 
              onClick={handleSubmit} 
              disabled={loading || (!selectedFile && !editMode)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  {editMode ? <Edit size={16} className="me-2" /> : <PlusCircle size={16} className="me-2" />}
                  {editMode ? '💾 Cập nhật' : '➕ Thêm mới'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      {/* Custom CSS */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .hover-zoom {
          transition: transform 0.5s ease;
        }
        .hover-lift:hover .hover-zoom {
          transform: scale(1.1) rotate(5deg);
        }
        .bg-gradient-warning {
          background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
        }
      `}</style>
    </Fragment>
  );
};

export default ImageWheelMiniGameManagement;

